import React, { useCallback, useContext, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useDispatch, useSelector } from 'react-redux';
import { getJobCloseAtDialogOpen } from 'lib/selectors/getJobCloseAtDialog';
import BaseJobTimeDialog from 'components/BaseJobTimeDialog';
import { TickerContext } from 'components/TickerProvider';
import _get from 'lodash/get';
import { toISO, safeConvertToISO } from 'lib/utils/date';
import { getCompany } from 'lib/selectors/getCompany';
import { getMachine } from 'lib/selectors/getMachine';
import { getLatestActivity } from 'lib/selectors/getLatestActivity';
import { actionCloseJobCloseAtDialog } from 'lib/actions';
import useTimeOptions from 'lib/hooks/useTimeOptions';
import {
  TIME_OPTION_FLOW,
  buildSkeletonOptions,
  buildFailureOptions,
  mapApiOptionsToUiOptions,
} from 'lib/utils/timeOptions';
import JobCloseAtDialogEventEmitter from './emitter';

const JobCloseAtDialogBase = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const nowMs = useContext(TickerContext);

  const company = useSelector(getCompany);
  const machine = useSelector(getMachine);
  const latestActivity = useSelector(getLatestActivity);
  const machineRef = machine?.machineRef;
  const tzid = _get(company, 'timezone');

  const openedAtRef = useRef(null);
  if (!openedAtRef.current) {
    openedAtRef.current = toISO(nowMs);
  }

  const jobStartedAtRef = useRef(null);
  if (!jobStartedAtRef.current) {
    jobStartedAtRef.current =
      safeConvertToISO(latestActivity?.start, tzid) || undefined;
  }

  const nowISO = toISO(nowMs);

  const jobEndBody = useMemo(() => {
    const jobEndPayload = { openedAt: openedAtRef.current };
    if (jobStartedAtRef.current) {
      jobEndPayload.jobStartedAt = jobStartedAtRef.current;
    }
    return { jobEnd: jobEndPayload };
  }, []);

  const { error, options } = useTimeOptions({
    enabled: !!machineRef,
    machineRef,
    flow: TIME_OPTION_FLOW.JOB_END,
    body: jobEndBody,
  });

  const optionsList = useMemo(() => {
    if (error || options?.length === 0) {
      return buildFailureOptions(TIME_OPTION_FLOW.JOB_END, t, nowISO);
    }

    if (!options) {
      return buildSkeletonOptions(TIME_OPTION_FLOW.JOB_END, t, nowISO);
    }

    return mapApiOptionsToUiOptions(
      options,
      TIME_OPTION_FLOW.JOB_END,
      t,
      nowISO
    );
  }, [error, options, t, nowISO]);

  const closeJobCloseAtDialog = useCallback(() => {
    dispatch(actionCloseJobCloseAtDialog());
  }, [dispatch]);

  const handleClose = useCallback(() => {
    JobCloseAtDialogEventEmitter.fireAndForget('cancel');
    closeJobCloseAtDialog();
  }, [closeJobCloseAtDialog]);

  const handleSelect = useCallback(
    (selectedValue) => {
      JobCloseAtDialogEventEmitter.fireAndForget('success', selectedValue);
      closeJobCloseAtDialog();
    },
    [closeJobCloseAtDialog]
  );

  return (
    <BaseJobTimeDialog
      headerText={t('Select job end time')}
      optionsList={optionsList}
      tzid={tzid}
      onClose={handleClose}
      onSelect={handleSelect}
    />
  );
};

const JobCloseAtDialog = () => {
  const isOpen = useSelector(getJobCloseAtDialogOpen);

  if (!isOpen) return null;

  return <JobCloseAtDialogBase />;
};

export default JobCloseAtDialog;
